import {
  useStore,
  getSmoothStepPath,
  getOutgoers,
  getIncomers,
  useReactFlow,
  BaseEdge,
  getNodesBounds,
} from "reactflow";
import { useCallback, useRef } from "react";
import { EDGE_TYPES, NODE_TYPES, COMMON_COMPONENT_TYPES } from "constants/flowDesigner";
import { tryCatchWrapper } from "helpers/common";

const LEFT = "left";
const RIGHT = "right";
const TOP = "top";
const BOTTOM = "bottom";

const FloatingEdge = ({ id, source, target, markerEnd }) => {
  const { getNodes, getEdges, getNode } = useReactFlow();
  const sourceNode = useStore(useCallback((store) => store.nodeInternals.get(source), [source]));
  const targetNode = useStore(useCallback((store) => store.nodeInternals.get(target), [target]));
  const edgesWithoutFloating = getEdges().filter((edge) => edge.type !== EDGE_TYPES.FLOATING);
  const isBorderNode = useRef(false);

  const checkForIncomingMultiBranch = (node) => {
    const incomers = getIncomers(node, getNodes(), edgesWithoutFloating);

    // we don't need to check for multi branch nodes since the target node is seen the earliest
    if (targetNode.targetPosition === TOP && node.position.y < targetNode.position.y) return null;

    if (targetNode.targetPosition === LEFT && node.position.x < targetNode.position.x) return null;

    if (
      node.data?.type === COMMON_COMPONENT_TYPES.CONDITIONAL ||
      node.data?.type === COMMON_COMPONENT_TYPES.SELECTION
    )
      return node;

    for (node of incomers) {
      return checkForIncomingMultiBranch(node);
    }

    return null;
  };

  const checkForOutgoingMultiBranch = (node) => {
    const outgoers = getOutgoers(node, getNodes(), edgesWithoutFloating);

    // we don't need to check for multi branch nodes since the target node is seen the earliest
    if (targetNode.targetPosition === TOP && node.position.y > targetNode.position.y) return null;

    if (targetNode.targetPosition === LEFT && node.position.x > targetNode.position.x) return null;

    if (
      node.data?.type === COMMON_COMPONENT_TYPES.CONDITIONAL ||
      node.data?.type === COMMON_COMPONENT_TYPES.SELECTION
    )
      return node;

    for (node of outgoers) {
      return checkForOutgoingMultiBranch(node);
    }

    return null;
  };

  const calculateSourcePosition = () => {
    const edgesWithoutFloating = getEdges().filter((edge) => edge.type !== EDGE_TYPES.FLOATING);
    const sourcePosition = sourceNode.sourcePosition;
    const targetPosition = targetNode.targetPosition;
    const outgoers = getOutgoers(sourceNode, getNodes(), edgesWithoutFloating);
    isBorderNode.current = false;

    if (targetPosition === TOP) {
      // vertical mode
      // make it always left when source and target x axis is the same
      if (sourceNode.position.x === targetNode.position.x) return LEFT;

      if (sourceNode.position.y <= targetNode.position.y) {
        // means target node is at the bottom
        const outgoingMultiBranch = checkForOutgoingMultiBranch(sourceNode);
        const lowerTreeBounds = calculateLowerTreeBounds();

        if (outgoingMultiBranch) {
          // there is a multi branch blocking the line from source to target

          if (sourceNode.position.x < targetNode.position.x) {
            // source node is on the left side

            // the source is much smaller than the tree width below it, set to
            if (sourceNode.position.x <= lowerTreeBounds.x) return sourcePosition;

            return LEFT;
          }

          if (sourceNode.position.x > targetNode.position.x) {
            // source node is on the right side

            // the source node is much farther than the lower tree so just use the original
            if (sourceNode.position.x >= lowerTreeBounds.width + lowerTreeBounds.x)
              return sourcePosition;

            return RIGHT;
          }
        }

        // just default to source position because there arent any blocker below
        if (outgoers[0]?.type === NODE_TYPES.HIDDEN) {
          isBorderNode.current = true;

          return sourceNode.position.x < targetNode.position.x ? LEFT : RIGHT;
        }
      }

      return sourceNode.position.x < targetNode.position.x ? LEFT : RIGHT;
    } else if (targetPosition === LEFT) {
      // horizontal mode
      // make it always left when source and target x axis is the same
      if (sourceNode.position.y === targetNode.position.y) return BOTTOM;

      if (sourceNode.position.x <= targetNode.position.x) {
        // means the target node is at the right side
        const outgoingMultiBranch = checkForOutgoingMultiBranch(sourceNode);
        const lowerTreeBounds = calculateLowerTreeBounds();

        if (outgoingMultiBranch) {
          // there is a multi branch blocking the line from source to target
          if (sourceNode.position.y < targetNode.position.y) {
            // source node is at the top

            // the source is much smaller than the tree width below it, set to
            if (sourceNode.position.y < lowerTreeBounds.y) return sourcePosition;

            return TOP;
          }

          if (sourceNode.position.y > targetNode.position.y) {
            // source node is at the bottom

            // the source node is much farther than the lower tree so just use the original
            if (sourceNode.position.y >= lowerTreeBounds.height) return sourcePosition;

            return BOTTOM;
          }
        }

        if (outgoers[0]?.type === NODE_TYPES.HIDDEN) return sourcePosition;
      }

      return sourceNode.position.y < targetNode.position.y ? TOP : BOTTOM;
    }

    return sourceNode.sourcePosition;
  };

  const calculateSourceX = () => {
    const targetPosition = targetNode.targetPosition;
    const x = sourceNode.position.x;

    if (targetPosition === TOP) {
      switch (calculateSourcePosition()) {
        case RIGHT:
          return x + 20;
        case LEFT:
          return x - 20;
      }
    }

    if (targetPosition === LEFT) {
      switch (calculateSourcePosition()) {
        case BOTTOM:
        case TOP:
          return x + 20;
      }
    }

    return x;
  };

  const calculateSourceY = () => {
    const targetPosition = targetNode.targetPosition;
    const y = sourceNode.position.y;

    if (targetPosition === TOP) {
      switch (calculateSourcePosition()) {
        case RIGHT:
        case LEFT:
          return y + 25;
      }
    }

    if (targetPosition === LEFT) {
      switch (calculateSourcePosition()) {
        case BOTTOM:
          return y + 20;
        case TOP:
          return y - 20;
      }
    }

    return y;
  };

  const calculateTargetPosition = () => {
    const sourcePosition = sourceNode.sourcePosition;

    if (sourcePosition === BOTTOM) {
      if (sourceNode.position.x > targetNode.position.x) return RIGHT;

      return LEFT;
    } else if (sourcePosition === RIGHT) {
      if (sourceNode.position.y < targetNode.position.y) return TOP;

      return BOTTOM;
    }

    return targetNode.sourcePosition;
  };

  const calculateTargetX = () => {
    const sourcePosition = sourceNode.sourcePosition;

    if (sourcePosition === BOTTOM) {
      if (sourceNode.position.x > targetNode.position.x) return targetNode.position.x + 30;

      return targetNode.position.x - 30;
    }

    return targetNode.position.x + 20;
  };

  const calculateTargetY = () => {
    const sourcePosition = sourceNode.sourcePosition;

    if (sourcePosition === BOTTOM) return targetNode.position.y + 25;

    if (sourcePosition === RIGHT) {
      if (sourceNode.position.y < targetNode.position.y) return targetNode.position.y - 27;

      return targetNode.position.y + 27;
    }
  };

  const calculateUpperTreeBounds = (returnTree = false) => {
    const treeNodes = {};

    const getUpperNodes = (node) => {
      const incomers = getIncomers(node, getNodes(), edgesWithoutFloating);

      if (node.id === targetNode.id) return;

      incomers.forEach((node) => {
        if (!treeNodes[node.id]) treeNodes[node.id] = node;

        getUpperNodes(node);
      });
    };

    getUpperNodes(sourceNode);
    const bounds = getNodesBounds(Object.values(treeNodes));

    if (returnTree) {
      return {
        treeNodes,
        bounds,
      };
    }

    return bounds;
  };

  const calculateLowerTreeBounds = (newSourceNode, newTargetNodes) => {
    const treeNodes = {};

    const getLowerBounds = (node) => {
      const outgoers = getOutgoers(node, getNodes(), edgesWithoutFloating);

      if (newTargetNodes?.length && newTargetNodes.find((targetNode) => targetNode?.id === node.id))
        return;

      if (node.id === targetNode.id) return;

      outgoers.forEach((node) => {
        if (!treeNodes[node.id]) treeNodes[node.id] = node;

        getLowerBounds(node);
      });
    };

    getLowerBounds(newSourceNode ?? sourceNode);

    return getNodesBounds(Object.values(treeNodes));
  };

  const getLargestUpperTreeBounds = () => {
    const { treeNodes } = calculateUpperTreeBounds(true);

    const multiBranches = Object.values(treeNodes)
      .filter(
        (node) =>
          node.data?.type === COMMON_COMPONENT_TYPES.SELECTION ||
          node.data?.type === COMMON_COMPONENT_TYPES.CONDITIONAL
      )
      .filter((node) => !!node.data.flowElement.data)
      .map((node) => ({
        treeId: node.id,
        endNodeId: node.data.flowElement.data?.branches?.length
          ? node.data.flowElement.data.branches[0].endPoint
          : "",
      }))
      .map((tree) =>
        calculateLowerTreeBounds(getNode(tree.treeId), [getNode(tree.endNodeId), sourceNode])
      );

    if (multiBranches.length) {
      const largestMultiBranch = multiBranches.reduce(
        (curr, accu) => (curr.width > accu.width ? curr : accu),
        multiBranches[0]
      );

      return largestMultiBranch;
    }

    return null;
  };

  const calculateOffset = () => {
    const outgoingMultiBranch = checkForOutgoingMultiBranch(sourceNode);
    const incomingMultiBranch = checkForIncomingMultiBranch(sourceNode);

    if (targetNode.targetPosition === TOP) {
      // vertical
      if (sourceNode.position.y <= targetNode.position.y) {
        // target is at the bottom
        if (outgoingMultiBranch && outgoingMultiBranch.position.y < targetNode.position.y) {
          // multi branch blocking
          const lowerTreeBounds = calculateLowerTreeBounds();

          // same axis so just get the distance between tree x axis and source node
          if (sourceNode.position.x === targetNode.position.x)
            return sourceNode.position.x - lowerTreeBounds.x + sourceNode.width;

          if (sourceNode.position.x > targetNode.position.x) {
            const offset = lowerTreeBounds.width + lowerTreeBounds.x - sourceNode.position.x;

            return offset < 0 ? 30 : offset;
          }

          if (lowerTreeBounds.x < sourceNode.position.x)
            return sourceNode.position.x - lowerTreeBounds.x + sourceNode.width;

          const offset = sourceNode.position.x - lowerTreeBounds.x;

          return offset < 0 ? 30 : offset;
        }
      }

      if (incomingMultiBranch && incomingMultiBranch.position.y >= targetNode.position.y) {
        // target is at the top with a multi branch blocking
        const upperTreeBounds = calculateUpperTreeBounds();
        const largestUpperTreeBounds = tryCatchWrapper(getLargestUpperTreeBounds)();
        const treeToUse =
          largestUpperTreeBounds && largestUpperTreeBounds.width > upperTreeBounds.width
            ? largestUpperTreeBounds
            : upperTreeBounds;

        if (sourceNode.position.x === targetNode.position.x)
          return sourceNode.position.x - treeToUse.x + sourceNode.width;

        if (sourceNode.position.x > targetNode.position.x)
          return treeToUse.width + treeToUse.x - sourceNode.position.x;

        return sourceNode.position.x - treeToUse.x + 10 + sourceNode.width;
      }
    }

    if (targetNode.targetPosition === LEFT) {
      // horizontal
      if (sourceNode.position.x <= targetNode.position.x) {
        // target is at the right side

        if (outgoingMultiBranch && outgoingMultiBranch.position.x < targetNode.position.x) {
          // multi branch blocking
          const lowerTreeBounds = calculateLowerTreeBounds();

          if (sourceNode.position.y === targetNode.position.y)
            return lowerTreeBounds.height + lowerTreeBounds.y - sourceNode.position.y;

          // means there is a multi branch at the bottom blocking the line from source and target
          if (sourceNode.position.y > targetNode.position.y) {
            const offset = lowerTreeBounds.height + lowerTreeBounds.y - sourceNode.position.y;

            return offset <= 0 ? 20 : offset;
          }

          // need to check if it is lesser than 0 because it pushes it farther
          const offset = sourceNode.position.y - lowerTreeBounds.y + sourceNode.height;

          return offset <= 0 ? 20 : offset;
        }
      }

      if (incomingMultiBranch && incomingMultiBranch.position.x >= targetNode.position.x) {
        // target is at the left with a multi branch blocking
        const upperTreeBounds = calculateUpperTreeBounds();
        const largestUpperTreeBounds = tryCatchWrapper(getLargestUpperTreeBounds)();
        const treeToUse =
          largestUpperTreeBounds && largestUpperTreeBounds.height > upperTreeBounds.height
            ? largestUpperTreeBounds
            : upperTreeBounds;

        if (sourceNode.position.y >= targetNode.position.y)
          return treeToUse.height + treeToUse.y - sourceNode.position.y;

        return sourceNode.position.y - treeToUse.y + sourceNode.height;
      }
    }

    return isBorderNode.current ? sourceNode.height - 15 : sourceNode.height;
  };

  const [path] = getSmoothStepPath({
    sourceX: calculateSourceX(),
    sourceY: calculateSourceY(),
    sourcePosition: calculateSourcePosition(),
    targetX: calculateTargetX(),
    targetY: calculateTargetY(),
    targetPosition: calculateTargetPosition(),
    offset: calculateOffset(),
  });

  return (
    <BaseEdge
      markerEnd={markerEnd}
      path={path}
      id={id}
      className="react-flow__edge-path"
      style={{
        transform:
          sourceNode.sourcePosition === BOTTOM
            ? "translateX(30px)"
            : "translateY(27px) translateX(10px)",
        stroke: "#7443FF",
        strokeDasharray: 2,
      }}
    />
  );
};

export default FloatingEdge;
